import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SCHOOL_YEAR_ACTIVE_ID,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import {
  Permission,
  PermissionAction,
  ProviderFilter as ProviderFilterModel,
  ProviderListDto,
  ProviderListFilter,
  ProviderListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { ProviderTable } from "./ProviderTable";
import { ProviderFilter } from "../../ProviderFilter";
import produce from "immer";
import { ListFooter } from "components";
import { providerCreatePageRoute } from "../ProviderCreatePage/ProviderCreatePage";

const routeName = "providers";

export const providerListPageRoute: PageRoute = {
  handle: "Providers",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

const ROOT_PERMISSION: Permission = Permission.Provider;

export const ProviderListPage = () => {
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<ProviderListFilter>({
    sortObjects: [{ property: "fullName", isDesc: false }],
    providerFilter: {
      isActive: true,
    },
  });

  const [initListFilter] = useState<ProviderListFilter>(listFilter);
  const [listItems, setListItems] = useState<ProviderListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<ProviderListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.providers.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.providers.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.providers.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<ProviderListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <ProviderFilter
        filter={listFilter.providerFilter || ({} as ProviderFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.providerFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.providers.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{providerListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() => navigate(providerCreatePageRoute.getPath({ screenType: "standard" }))}
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <ProviderTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              providers={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
